import React, { useMemo, useState } from "react";
import { Table, ConfigProvider } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
    ChevronDown,
    ChevronRight,
    Sun,
    Moon,
    Wallet,
    FolderKanban,
    ShoppingCart,
    UserPlus,
    Megaphone,
    UserCog,
    MoreHorizontal,
    Users,
    Handshake,
    Zap,
    TrendingUp,
    Layers,
} from "lucide-react";
import { mixformatCurrency } from "services/mixin";

interface RowData {
    id: string;
    name: string;
    year: PV;
    quarters: PV[]; // length 4
    months: PV[]; // length 12
}

interface GroupData {
    id: string;
    name: string;
    year: PV;
    quarters: PV[];
    months: PV[];
    rows: RowData[];
}

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
type PV = { qty: number | null; plan: number | null };
type TableRow = {
    key: string;
    name: string;
    isGroup?: boolean;
    year: PV;
    periods: PV[];
    children?: TableRow[];
};

const QUARTER_LABELS = ["Quý 1", "Quý 2", "Quý 3", "Quý 4"];
const MONTH_LABELS = Array.from({ length: 12 }, (_, i) => `Tháng ${i + 1}`);
const N = (qty: number | null, plan: number | null): PV => ({ qty, plan });
const nulls = (n: number): PV[] => Array.from({ length: n }, () => N(null, null));

/* ------------------------------------------------------------------ */
/*  Data (transcribed from the two source tables)                     */
/* ------------------------------------------------------------------ */

const DATA: GroupData[] = [
    {
        id: "khac",
        name: "Thu theo dự án",
        year: N(1, 1_000_000_000),
        quarters: [N(615, 417_850_000), N(17, 1_208_000_000), N(2444, 1_054_916_666), N(null, null)],
        months: [
            N(203, 134_500_000),
            N(199, 188_050_000),
            N(213, 95_300_000),
            N(2, 30_000_000),
            N(null, null),
            ...nulls(7),
        ],
        rows: [
            {
                id: "chi-khac",
                name: "Doanh thu",
                year: N(1, 666_666_666),
                quarters: [N(null, null), N(null, null), N(1, 666_666_666), N(null, null)],
                months: nulls(12),
            },
        ],
    },
    {
        id: "chi-theo-du-an",
        name: "Khác",
        year: N(3, 6_000_000_000),
        quarters: [N(null, null), N(null, null), N(3, 6_000_000_000), N(null, null)],
        months: nulls(12),
        rows: [
            {
                id: "chi-khac-theo-du-an",
                name: "Thu theo dự án",
                year: N(3, 6_000_000_000),
                quarters: [N(null, null), N(null, null), N(3, 6_000_000_000), N(null, null)],
                months: nulls(12),
            },
        ],
    },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

export default function ExpensePlan({ view }: { view: string }) {

    const periodLabels = view === "quarter" ? QUARTER_LABELS : view === "month" ? MONTH_LABELS : [];

    const tableData: TableRow[] = useMemo(() => {
        return DATA.map((group) => ({
            key: group.id,
            name: group.name,
            isGroup: true,
            year: group.year,
            periods: view === "quarter" ? group.quarters : group.months,
            children: group.rows.map((row) => ({
                key: row.id,
                name: row.name,
                year: row.year,
                periods: view === "quarter" ? row.quarters : row.months,
            })),
        }));
    }, [view]);

    const columns: ColumnsType<TableRow> = useMemo(() => {
        const cols: ColumnsType<TableRow> = [
            {
                title: "Loại thu",
                dataIndex: "name",
                key: "name",
                fixed: "left",
                width: 280,
                render: (_, record) => {
                    return (
                        <div className="flex items-center gap-2">
                            <span className={record.isGroup ? "font-semibold" : ""}>
                                {record.name}
                            </span>
                        </div>
                    );
                },
            },
            {
                title: "Năm 2026",
                fixed: "left",
                children: [
                    {
                        title: "Số lượng",
                        width: 110,
                        align: "right",
                        render: (_, r) => {
                            return r.year.qty === null || r.year.qty === undefined || typeof r.year.qty !== "number" || Number.isNaN(r.year.qty) ? (
                                <span>--</span>
                            ): (
                                mixformatCurrency(r.year.qty,false)
                            )
                        },
                    },
                    {
                        title: "Kế hoạch",
                        width: 140,
                        align: "right",
                        render: (_, r) => {
                            return r.year.plan === null || r.year.plan === undefined || typeof r.year.plan !== "number" || Number.isNaN(r.year.plan) ? (
                                <span>--</span>
                            ): (
                                mixformatCurrency(r.year.plan,false)
                            )
                        },
                    },
                ],
            },
        ];

        periodLabels.forEach((label, i) => {
            cols.push({
                title: label,
                children: [
                    {
                        title: "Số lượng",
                        width: 110,
                        align: "right",
                        // render: (_, r) => mixformatCurrency(r.periods[i]?.qty, false),
                        render: (_, r) => {
                            return r.periods[i]?.qty === null || r.periods[i]?.qty === undefined || typeof r.periods[i]?.qty !== "number" || Number.isNaN(r.periods[i]?.qty) ? (
                                <span>--</span>
                            ): (
                                mixformatCurrency(r.periods[i]?.qty,false)
                            )
                        },
                    },
                    {
                        title: "Kế hoạch",
                        width: 140,
                        align: "right",
                        // render: (_, r) => {mixformatCurrency(r.periods[i]?.plan,false)},
                        render: (_, r) => {
                            return r.periods[i]?.plan === null || r.periods[i]?.plan === undefined || typeof r.periods[i]?.plan !== "number" || Number.isNaN(r.periods[i]?.plan) ? (
                                <span>--</span>
                            ): (
                                mixformatCurrency(r.periods[i]?.plan,false)
                            )
                        },
                    },
                ],
            });
        });

        return cols;
    }, [periodLabels]);

    // const grandTotalPlan = useMemo(
    //     () => DATA.reduce((s, g) => s + (g.year.plan ?? 0), 0),
    //     []
    // );
    // const grandTotalQty = useMemo(() => DATA.reduce((s, g) => s + (g.year.qty ?? 0), 0), []);

    return (
        <div>
            {/* ---------------- Table ---------------- */}
            <div className="mt-4">

                <ConfigProvider
                    theme={{
                        components: {
                            Table: {
                                headerBg: "#f8fafc",
                                headerColor: "#64748b",
                                borderColor: "#e5e7eb",
                                rowHoverBg: "#f8fafc",
                                cellPaddingBlock: 10,
                            },
                        },
                    }}
                >
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        pagination={false}
                        bordered
                        size="middle"
                        scroll={{ x: "max-content" }}
                        expandable={{
                            defaultExpandAllRows: true,
                            indentSize: 28,
                        }}
                        rowClassName={(record) => (record.isGroup ? "group-row" : "")}
                    />
                </ConfigProvider>
            </div>
            {/* <p className="mt-3 text-center text-xs text-slate-400 dark:text-slate-600">
                Cuộn ngang để xem thêm kỳ báo cáo · Nhấn vào tên nhóm để thu gọn / mở rộng
            </p> */}
        </div>
    )
}